import { useLayoutEffect } from 'react';
import { browserHistory } from 'react-router';

import { get } from 'lodash';

import { ProvisioningEnum, RolloutEnum } from '@lib/resources/types';

import usePermissions from 'hooks/usePermissions';
import { SETTINGS_ITEMS, Tab } from 'screens/MissionControl/constants/settingsItems';

import useControlUserAccess from './useControlUserAccess';

const flatMenu = (items: Tab[]) => {
  let arr = items;

  items.forEach((item) => {
    arr = [...arr, item];
    if (Array.isArray(item.subMenu)) {
      arr = [...arr, ...flatMenu(item.subMenu)];
    }
  });
  // remove dups
  return arr.filter((a, idx) => arr.indexOf(a) === idx && !a.subMenu);
};

const flatSettingsItems = flatMenu(SETTINGS_ITEMS);

export default function useMissionControlRedirect(pathName: string) {
  const {
    home: { myAccount, mainAccount },
    securityCenter,
    practiceResources,
    featureSettings,
    utilities,
  } = useControlUserAccess();
  const { hasAccessToProduct } = usePermissions();

  const getPathObject = flatSettingsItems.find((item) => get(item, 'path', '').includes(pathName));
  const provisioning = get(getPathObject, 'provisioning', []);
  const provisioningExcluded = get(getPathObject, 'provisioningExcluded', []);

  const isBillingRoute = /v2\/settings\/utilities\/patient-billing/.test(pathName);
  const hasNotBillingAccess =
    isBillingRoute && !hasAccessToProduct([ProvisioningEnum.LumaPay, ProvisioningEnum.InsuranceVerification]);

  const isLumaBotRoute = /v2\/settings\/features\/lumabot/.test(pathName);
  const hasNotLumaBotAccess = isLumaBotRoute && !hasAccessToProduct(ProvisioningEnum.LumaBot);

  const isEHRIntegrationRoute = /v2\/settings\/utilities\/third-party-integration\/ehr-integration/.test(pathName);
  const hasNotEHRIntegrationAccess = isEHRIntegrationRoute && !hasAccessToProduct(RolloutEnum.EHRIntegrators);

  const hasNotChecklistBuilderAccess =
    /v2\/settings\/utilities\/checklist-builder/.test(pathName) && !utilities.checklistBuilder.canView;

  const hasNotInsuranceEligibilitySetupAccess =
    /v2\/settings\/utilities\/eligibility-setup/.test(pathName) && !utilities.eligibilitySetup.canView;

  const isCARoute = /v2\/settings\/utilities\/custom-actions/.test(pathName);
  const isNotFollowUpProv =
    isCARoute && /appointment|procedure|diagnostic/i.test(pathName) && !hasAccessToProduct(ProvisioningEnum.Followup);
  const isNotReminderProv = isCARoute && /reminder/i.test(pathName) && !hasAccessToProduct(ProvisioningEnum.Reminder);
  const isNotRecallProv = isCARoute && /recall/i.test(pathName) && !hasAccessToProduct(ProvisioningEnum.Recall);

  const isNotProvisioned =
    isNotFollowUpProv ||
    isNotReminderProv ||
    isNotRecallProv ||
    hasNotBillingAccess ||
    hasNotLumaBotAccess ||
    hasNotEHRIntegrationAccess ||
    hasNotChecklistBuilderAccess ||
    hasNotInsuranceEligibilitySetupAccess ||
    (provisioning.length && !hasAccessToProduct(provisioning)) ||
    (provisioningExcluded?.length && hasAccessToProduct(provisioningExcluded));

  const hasNotMyAccountAccess = /v2\/settings\/home\/my-account/.test(pathName) && !myAccount.canView;
  const hasNotMainAccountAccess = /v2\/settings\/home\/main-account/.test(pathName) && !mainAccount.canView;
  const hasNotSecurityCenterAccess = /v2\/settings\/security-center/.test(pathName) && !securityCenter.canView;
  const hasNotPracticeResourcesAccess = /v2\/settings\/practice-resources/.test(pathName) && !practiceResources.canView;
  const hasNotFeaturesSettingsAccess = /v2\/settings\/features/.test(pathName) && !featureSettings.canView;
  const hasNotUtilitiesAccess = /v2\/settings\/utilities/.test(pathName) && !utilities.canView;

  const isMissionControl = /v2\/settings/.test(pathName);

  const hasNoAccess = Boolean(
    isMissionControl &&
      (hasNotMyAccountAccess ||
        hasNotMainAccountAccess ||
        hasNotSecurityCenterAccess ||
        hasNotPracticeResourcesAccess ||
        hasNotFeaturesSettingsAccess ||
        hasNotUtilitiesAccess ||
        isNotProvisioned),
  );

  useLayoutEffect(() => {
    if (hasNoAccess) {
      browserHistory.push('/v2/settings/home');
    }
  }, [hasNoAccess]);

  return hasNoAccess;
}
